import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const STATUS_LABEL: Record<string, string> = { active: 'Ativo', suspended: 'Suspenso' };
const STATUS_PILL: Record<string, string> = { active: 'pillPos', suspended: 'pillDanger' };
const RISK_LABEL: Record<string, string> = { normal: 'Normal', watch: 'Observação', high: 'Alto' };
const RISK_PILL: Record<string, string> = { normal: 'pillMuted', watch: 'pillWarn', high: 'pillDanger' };
const STORE_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  unpublished: 'Despublicada',
  suspended: 'Suspensa',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function pct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** Admin · drill-down de um parceiro — dados cadastrais + cada loja com GMV/comissão/chargeback.
 *  Consome `GET /api/admin/partners/{id}`. */
export function PartnerDetail() {
  const { id } = useParams<{ id: string }>();
  const partner = useQuery({
    queryKey: ['admin', 'partner', id],
    queryFn: () => api.partner(id!),
    enabled: !!id,
  });
  const p = partner.data;

  return (
    <>
      <div className={shellStyles.head}>
        <Link className={ui.faint} to="/parceiros">
          ← Parceiros
        </Link>
        <h1>{p?.name ?? 'Parceiro'}</h1>
        <p>{p?.email ?? ''}</p>
      </div>

      {partner.isLoading ? (
        <p className={ui.empty}>Carregando…</p>
      ) : !p ? (
        <p className={ui.empty}>Parceiro não encontrado.</p>
      ) : (
        <>
          <section className={ui.stats} aria-label="Dados do parceiro">
            <div className={ui.stat}>
              <span className={ui.statLbl}>Status</span>
              <div className={ui.statVal}>
                <span className={`${ui.pill} ${ui[STATUS_PILL[p.status] ?? 'pillMuted']}`}>
                  {STATUS_LABEL[p.status] ?? p.status}
                </span>
              </div>
            </div>
            <div className={ui.stat}>
              <span className={ui.statLbl}>Risco</span>
              <div className={ui.statVal}>
                <span className={`${ui.pill} ${ui[RISK_PILL[p.risk] ?? 'pillMuted']}`}>
                  {RISK_LABEL[p.risk] ?? p.risk}
                </span>
              </div>
            </div>
            <div className={ui.stat}>
              <span className={ui.statLbl}>CPF</span>
              <div className={ui.statVal} style={{ fontSize: 16 }}>
                <span className={ui.mono}>{p.cpf || '—'}</span>
              </div>
            </div>
            <div className={ui.stat}>
              <span className={ui.statLbl}>Telefone</span>
              <div className={ui.statVal} style={{ fontSize: 16 }}>
                <span className={ui.mono}>{p.phone || '—'}</span>
              </div>
            </div>
            <div className={ui.stat}>
              <span className={ui.statLbl}>Cadastro</span>
              <div className={ui.statVal} style={{ fontSize: 16 }}>{when(p.createdAt)}</div>
            </div>
          </section>

          <section>
            <div className={ui.secH}>
              <h2>Lojas</h2>
            </div>
            <div className={ui.card}>
              {!p.stores.length ? (
                <p className={ui.empty}>Nenhuma loja criada ainda.</p>
              ) : (
                <div className={ui.tblScroll}>
                  <table className={ui.tbl}>
                    <thead>
                      <tr>
                        <th>Loja</th>
                        <th>Status</th>
                        <th className="r">Pedidos pagos</th>
                        <th className="r">GMV</th>
                        <th className="r">Comissão</th>
                        <th className="r">Chargeback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.stores.map((store) => (
                        <tr key={store.id}>
                          <td>
                            <b>{store.name}</b>
                            <div className={ui.faint}>{store.subdomain}</div>
                          </td>
                          <td className={ui.muted}>
                            {(store.status && STORE_STATUS_LABEL[store.status]) ?? store.status}
                          </td>
                          <td className="r">
                            <span className={ui.mono}>{store.paidOrders ?? 0}</span>
                          </td>
                          <td className="r">
                            <span className={ui.money}>{money(store.gmv ?? 0)}</span>
                          </td>
                          <td className="r">
                            <span className={ui.money}>{money(store.commission ?? 0)}</span>
                          </td>
                          <td className="r">
                            <span className={ui.mono}>{pct(store.chargebackRate ?? 0)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}
